import { useState } from 'react'
import { Check, Loader2, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  FORM_CATEGORY_LABELS,
  FORM_CATEGORY_OPTIONS,
  FORM_CATEGORY_COLORS,
  type FormCategory,
  type CgtFormType,
  type FormDefinition,
  type FieldDefinition,
  type SectionDefinition,
  type FormulaGate,
  type FormFieldLayout,
  normalizeAttachment,
} from '@/types/cgt'
import {
  DEFAULT_LABEL_POSITION,
  DEFAULT_LABEL_WIDTH,
  isLabelPosition,
  isLabelWidth,
  supportsLabelLayout,
} from '@/lib/fieldLayout'
import { LabelLayoutPicker } from './LabelLayoutPicker'
import { toCgtFieldType } from './field-config'
import { formsApi, isNewVersionRequired } from '@/api/formsApi'
import { extractFormulaRefs } from '@/lib/formula'
import { sanitizeVisibilityRule, sanitizeNotificationRules, notificationRules } from '@/lib/formSections'
import { sanitizeOosConfig, sanitizeTableOos } from '@/lib/oos'
import { FormActionChecklist } from './FormActionChecklist'
import { GateFormulaEditor } from './GateFormulaEditor'
import { tableFieldRefLabels } from './formulaRefPicker'
import { UserGroupMultiSelect } from '@/components/form-builder/UserGroupMultiSelect'
import type { FormField, FormSection, FormMeta } from './types'

const CATEGORY_OPTIONS = FORM_CATEGORY_OPTIONS

interface Props {
  formMeta: FormMeta
  sections: FormSection[]
  // Category and actions are owned by the parent (FormEditorPage's defMeta) so
  // the toolbar badge, toolbar save, and this panel can never disagree.
  category: FormCategory
  allowedActions: string[]
  /** Paired with the Repeat action; null = does not repeat. */
  defaultRepeatCount?: number | null
  completionGate?: FormulaGate
  /** UserGroup ids allowed to complete the form; empty = unrestricted. */
  completerGroupIds?: string[]
  onFormMetaChange: (patch: Partial<FormMeta>) => void
  onCategoryChange: (category: FormCategory) => void
  /** The count rides along when Repeat is toggled, so the pair stays atomic. */
  onAllowedActionsChange: (allowedActions: string[], defaultRepeatCount?: number | null) => void
  onDefaultRepeatCountChange: (next: number | null) => void
  onCompletionGateChange: (gate: FormulaGate | undefined) => void
  onCompleterGroupIdsChange: (ids: string[]) => void
  /** Form-wide label default (z8nrz7djyx); null = the system default. */
  fieldLayout?: FormFieldLayout | null
  onFieldLayoutChange?: (fieldLayout: FormFieldLayout | null) => void
  onSaved: (definition: FormDefinition) => void
  /** When set, Save updates this form in place instead of creating a new one. */
  definitionId?: string
}

/** Converts a single builder FormField into a CGT FieldDefinition. */
function toFieldDefinition(f: FormField, index: number): FieldDefinition {
  const isCalculated = f.type === 'calculated'
  const isTable = f.type === 'table'
  // A select field either references an Option Set (single source of truth — no
  // inline options emitted) or carries its own inline options.
  const usesOptionSet = f.optionsSource === 'optionSet' && !!f.optionSetId
  return {
    id: f.id,
    label: f.label,
    // Calculated and MR fields are never required (enforced server-side too).
    fieldType: toCgtFieldType(f.type),
    // Coerce to a real boolean — the API rejects a missing/undefined value.
    required: isCalculated || f.isMrField ? false : !!f.required,
    helpText: f.helpText || undefined,
    order: index,
    colSpan: isTable ? 2 : f.colSpan,
    fieldWidth: isTable ? 'full' : f.fieldWidth,
    // Only for types that draw a label row, and only valid values, so a type
    // change cannot leave a stale override behind.
    labelPosition:
      supportsLabelLayout(f.type) && isLabelPosition(f.labelPosition) ? f.labelPosition : undefined,
    labelWidth: supportsLabelLayout(f.type) && isLabelWidth(f.labelWidth) ? f.labelWidth : undefined,
    options: usesOptionSet
      ? undefined
      : f.options.length > 0
        ? f.options.map(o => o.label)
        : undefined,
    optionSetId: usesOptionSet ? f.optionSetId : undefined,
    formula: isCalculated ? f.formula : undefined,
    formulaRefs:
      isCalculated && f.formula ? extractFormulaRefs(f.formula) : undefined,
    displayFormat: isCalculated ? f.displayFormat : undefined,
    requiredGroup: f.requiredGroup,
    allowedSignerGroupIds: f.type === 'signature' ? f.allowedSignerGroupIds : undefined,
    distinctSignerSet:
      f.type === 'signature' && f.distinctSignerSet?.trim() ? f.distinctSignerSet.trim() : undefined,
    isGate: f.isGate,
    gatedFormType: f.gatedFormType as CgtFormType | undefined,
    anchored: f.anchored,
    // A table's stage lives on its columns and cells, never on the field.
    lisStage: !isTable && f.lisStage ? f.lisStage : undefined,
    lisTestCode: !isTable && f.lisTestCode ? f.lisTestCode : undefined,
    // Only ever sent as an explicit `false`; `true` is the default and would
    // just be noise on every field in every definition.
    canChange: f.canChange === false ? false : undefined,
    isMrField: f.isMrField,
    // The key is only meaningful while the MR flag is on.
    mrFieldKey: f.isMrField ? f.mrFieldKey : undefined,
    // Column and cell specifications are cleaned the way a field's is.
    tableConfig: isTable && f.tableConfig ? sanitizeTableOos(f.tableConfig) : undefined,
    // Provenance — carried through so the backend "used by forms" query can find it.
    libraryFieldId: f.libraryFieldId,
    unlockedBy: f.unlockedBy,
    // Half-edited condition rows (structured or formula) are stripped so the
    // API never sees them.
    visibility: sanitizeVisibilityRule(f.visibility),
    // Independent of visibility — half-edited notification rules are stripped
    // the same way (incomplete conditions, blank message, no channel, nobody
    // to send to). Always written as the LIST shape, so a field last edited
    // under the single-rule shape is normalized on its next save.
    notifications: sanitizeNotificationRules(notificationRules(f)),
    // Cleared alongside, so the legacy key cannot outlive the list and be read
    // back by anything still looking at it.
    notification: undefined,
    unlockGate: f.unlockGate,
    signatureGate: f.signatureGate,
    // Supported types only (see lib/oos.ts); absent when the author set none,
    // so a field without a specification saves exactly as before.
    oos: sanitizeOosConfig(toCgtFieldType(f.type), f.oos, isCalculated ? f.displayFormat : undefined),
    // Dropped for types that can't carry one, and `required` cleared whenever the
    // attachment is off, so the API never sees an orphaned flag.
    attachment: normalizeAttachment(toCgtFieldType(f.type), f.attachment),
    // Only a description carries one; anything else would be dead weight in the
    // definition and a second place a value could hide.
    constantText: f.type === 'description' ? (f.constantText ?? '') : undefined,
  }
}

/** Converts the builder's FormSection[] into CGT SectionDefinition[] for the API. */
export function toSectionDefinitions(sections: FormSection[]): SectionDefinition[] {
  return sections.map((s, si) => ({
    id: s.id,
    title: s.title,
    width: s.width,
    headerColor: s.headerColor,
    order: si,
    // Section-level sequencing locks — persisted so they survive reload.
    gatedByPrevious: s.gatedByPrevious,
    unlockedBySections: s.unlockedBySections,
    visibility: sanitizeVisibilityRule(s.visibility),
    fields: s.fields.map(toFieldDefinition),
  }))
}

export function FormDefinitionPanel({
  formMeta,
  sections,
  category,
  allowedActions,
  defaultRepeatCount,
  completionGate,
  completerGroupIds = [],
  onFormMetaChange,
  onCategoryChange,
  onAllowedActionsChange,
  onDefaultRepeatCountChange,
  onCompletionGateChange,
  onCompleterGroupIdsChange,
  fieldLayout = null,
  onFieldLayoutChange,
  onSaved,
  definitionId,
}: Props) {
  const [saving, setSaving] = useState(false)
  const [savedId, setSavedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  function handleCategoryChange(c: FormCategory) {
    onCategoryChange(c)
    setSavedId(null)
    setError(null)
  }

  async function handleSave() {
    if (!formMeta.name.trim()) {
      setError('Form name is required.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = {
        name: formMeta.name.trim(),
        category,
        allowedActions,
        // Explicit null rather than omitted: absent means "leave unchanged" at
        // the DTO edge, so unchecking Repeat has to SEND the cleared value.
        defaultRepeatCount: defaultRepeatCount ?? null,
        // Same: an absent key keeps the stored gate, so turning it off sends null.
        completionGate: completionGate ?? null,
        completerGroupIds,
        // Explicit null clears a removed default; absent would keep it.
        fieldLayout,
        sections: toSectionDefinitions(sections),
      }
      const saved = definitionId
        ? await formsApi.updateDefinition(definitionId, payload)
        : await formsApi.saveDefinition(payload)
      setSavedId(saved.id)
      onSaved(saved)
    } catch (e) {
      // This panel has no version dialog; the builder's Save form does.
      if (isNewVersionRequired(e)) setError('This form is in use. Use Save form to save these changes as a new version.')
      else setError(e instanceof Error ? e.message : 'Save failed.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='space-y-5'>
      {/* Form name */}
      <div className='space-y-1.5'>
        <Label>Form Name</Label>
        <Input
          value={formMeta.name}
          onChange={e => onFormMetaChange({ name: e.target.value })}
          placeholder='e.g. Patient Chart'
        />
      </div>

      {/* Form type / category */}
      <div className='space-y-1.5'>
        <Label>Form Type</Label>
        <Select value={category} onValueChange={v => handleCategoryChange(v as FormCategory)}>
          <SelectTrigger>
            <SelectValue placeholder='Select form type…' />
          </SelectTrigger>
          <SelectContent>
            {CATEGORY_OPTIONS.map(c => (
              <SelectItem
                key={c}
                value={c}
                style={{ backgroundColor: `${FORM_CATEGORY_COLORS[c]}1f` }}
              >
                <span className='flex items-center gap-2'>
                  <span
                    className='h-2.5 w-2.5 shrink-0 rounded-full'
                    style={{ backgroundColor: FORM_CATEGORY_COLORS[c] }}
                  />
                  {FORM_CATEGORY_LABELS[c]}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className='text-xs text-muted-foreground'>
          Classifies the form. The order forms appear in is defined per procedure, not here.
        </p>
      </div>

      {/* Allowed actions checklist */}
      <div className='space-y-1.5'>
        <Label>Actions</Label>
        <FormActionChecklist
          value={allowedActions}
          onChange={(next, nextRepeatCount) => {
            onAllowedActionsChange(next, nextRepeatCount)
            // Any change makes the saved-version banner stale, same as before.
            setSavedId(null)
          }}
          repeatCount={defaultRepeatCount}
          onRepeatCountChange={next => {
            onDefaultRepeatCountChange(next)
            setSavedId(null)
          }}
        />
        <p className='text-xs text-muted-foreground'>
          Actions shown in the patient procedure action menu for this form.
        </p>
      </div>

      {/* Who may complete the form (z8nrz7dt45) */}
      <UserGroupMultiSelect
        id='form-completer-groups'
        label='Allowed to complete'
        helpText='Only members of these groups can complete this form. Checked on the server.'
        value={completerGroupIds}
        onChange={onCompleterGroupIdsChange}
      />

      {/* Form-wide label default (z8nrz7djyx). Changing it never rewrites a
          field's own setting. */}
      {onFieldLayoutChange && (
        <div className='space-y-1.5'>
          <Label>Field labels</Label>
          <LabelLayoutPicker
            position={fieldLayout?.labelPosition ?? DEFAULT_LABEL_POSITION}
            width={fieldLayout?.labelWidth ?? DEFAULT_LABEL_WIDTH}
            onPositionChange={labelPosition => {
              onFieldLayoutChange({ ...fieldLayout, labelPosition })
              setSavedId(null)
            }}
            onWidthChange={labelWidth => {
              onFieldLayoutChange({ ...fieldLayout, labelWidth })
              setSavedId(null)
            }}
          />
          <p className='text-xs text-muted-foreground'>
            Where labels sit on this form. A field can set its own.
          </p>
          {/* null, not absent: the save sends it, and null clears the stored default. */}
          {fieldLayout && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 px-2 text-xs'
              onClick={() => {
                onFieldLayoutChange(null)
                setSavedId(null)
              }}
            >
              No default
            </Button>
          )}
        </div>
      )}

      {/* Whole-form completion gate */}
      <GateFormulaEditor
        label='Completion gate'
        helperText='A Boolean formula that must be true (or left off) before Complete Form succeeds — re-checked on the server.'
        gate={completionGate}
        onChange={onCompletionGateChange}
        blockedConsequence='Complete Form stays blocked'
        refGroups={[
          {
            key: '',
            name: 'This form',
            fields: sections
              .flatMap(s => s.fields)
              .filter(f => f.type !== 'table' && f.type !== 'section')
              .map(f => ({ refKey: f.id, label: f.label || 'Untitled', type: f.type })),
          },
        ]}
        labelById={{
          // Table cells and totals count as known refs, as in a calculated field.
          ...tableFieldRefLabels(sections.flatMap(s => s.fields)),
          ...Object.fromEntries(sections.flatMap(s => s.fields).map(f => [f.id, f.label || 'Untitled'])),
        }}
      />

      <Separator />

      {/* Save button */}
      <div className='space-y-2'>
        <Button className='w-full' onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className='h-4 w-4 mr-2 animate-spin' />
          ) : savedId ? (
            <Check className='h-4 w-4 mr-2 text-green-500' />
          ) : (
            <Save className='h-4 w-4 mr-2' />
          )}
          {saving ? 'Saving…' : 'Save Definition'}
        </Button>

        {error && (
          <p className='text-xs text-destructive'>{error}</p>
        )}

        {savedId && !error && (
          <div className='rounded-md bg-green-50 border border-green-200 px-3 py-2'>
            <p className='text-xs font-medium text-green-800'>Definition saved</p>
            <p className='text-xs text-green-700 font-mono mt-0.5 break-all'>{savedId}</p>
          </div>
        )}
      </div>
    </div>
  )
}
